import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import building from '../../../../../../public/assets/images/home-building.png';
import { Edit2, User } from 'iconsax-react';
import { yellow100, yellow300, yellowMain } from '../../../../constants/color';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { uploadProfilePictureAsync } from '@application/features/freelancer/freelancerThunks';
import { translate } from '@application/common/helpers/translate';
import { centered } from '../../account/style';
import { getColorAndBackground } from '@ui/constants/helper';
export const UserProfileCard = ({ profilePicture, profileStatus }) => {
    const { freelancer } = useAppSelector((state) => state.freelancer);
    const dispatch = useAppDispatch();
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const theme = useTheme();
    const [avatarImage, setAvatarImage] = useState(null);
    const handleImageChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (file) {
            const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            const maxSizeInMB = 1;
            const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
            if (!validTypes.includes(file.type)) {
                setError('Only PNG, JPEG, and JPG files are allowed.');
                return;
            }
            if (file.size > maxSizeInBytes) {
                setError(translate('validations.maxAllowedFileSize'));
                return;
            }
            setError(null);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarImage(reader.result);
            };
            reader.readAsDataURL(file);
            dispatch(uploadProfilePictureAsync(file));
        }
    };
    useEffect(() => {
        setAvatarImage(profilePicture);
    }, [profilePicture]);
    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const file = event.dataTransfer.files[0];
        if (file) {
            const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            const maxSizeInMB = 1;
            const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
            if (!validTypes.includes(file.type)) {
                setError('Only PNG, JPEG, and JPG files are allowed.');
                return;
            }
            if (file.size > maxSizeInBytes) {
                setError(translate('validations.maxAllowedFileSize'));
                return;
            }
            setError(null);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarImage(reader.result);
            };
            reader.readAsDataURL(file);
            dispatch(uploadProfilePictureAsync(file));
        }
    };
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    const handleEditClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    return (_jsxs(Box, { sx: {
            height: '350px',
            borderRadius: '25px',
            backgroundImage: `url(${building})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#fffff',
            opacity: 15,
            padding: '20px 20px',
            position: 'relative',
            cursor: 'pointer'
        }, onDrop: handleDrop, onDragOver: handleDragOver, children: [!avatarImage ? (_jsx("label", { htmlFor: 'avatarInput', children: _jsxs(Box, { sx: {
                        width: '135px',
                        height: '135px',
                        borderRadius: '50%',
                        border: `1px dashed ${theme.palette.primary.main}`,
                        background: yellow100,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 10
                    }, children: [_jsx(Box, { sx: {
                                zIndex: 1,
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }, children: _jsx(User, { style: { width: '80px', height: '80px', color: yellow300 } }) }), _jsx(Box, { sx: {
                                zIndex: 9,
                                textAlign: 'center'
                            }, children: _jsxs(Typography, { variant: 'subtitle2', color: yellowMain, children: ["Upload your profile picture ", _jsx("span", { style: { textDecoration: 'underline', cursor: 'pointer' }, children: "browse" })] }) })] }) })) : (_jsxs(Box, { position: 'relative', children: [_jsx(Avatar, { src: avatarImage, sx: { width: '130px', height: '130px', marginBottom: '10px' } }), _jsx(Box, { sx: {
                            position: 'absolute',
                            bottom: 20,
                            right: 10,
                            width: 30,
                            height: 30,
                            background: 'white',
                            borderRadius: '50%',
                            ...centered,
                            cursor: 'pointer'
                        }, onClick: handleEditClick, children: _jsx(Edit2, { size: '18px', color: theme.palette.primary.main }) })] })), _jsx("input", { id: 'avatarInput', type: 'file', accept: 'image/*', onChange: handleImageChange, style: { display: 'none' }, ref: fileInputRef }), error && (_jsx(Typography, { color: 'error', textAlign: 'center', variant: 'caption', children: error })), _jsx(Typography, { fontSize: '24px', textAlign: 'center', fontWeight: 500, color: '#141414', mt: error ? 0 : 2, children: (freelancer?.firstName ?? '') + ' ' + (freelancer?.lastName ?? '') }), (profileStatus || freelancer.contractType) && (_jsxs(Box, { sx: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }, children: [profileStatus && (_jsx(Chip, { size: 'small', label: `Profile ${profileStatus.toLowerCase()}`, sx: {
                            borderRadius: '25px',
                            fontWeight: 500,
                            color: getColorAndBackground(profileStatus).color,
                            backgroundColor: getColorAndBackground(profileStatus).background
                        } })), freelancer.contractType && (_jsx(Chip, { size: 'small', label: `Contract type: ${freelancer.contractType}`, style: { backgroundColor: theme.palette.primary.main, borderRadius: '25px', color: 'black' } }))] })), _jsxs(Box, { sx: {
                    position: 'absolute',
                    bottom: '-20px',
                    left: 0,
                    height: '80px',
                    width: '100%',
                    background: '#D9D9D9',
                    borderRadius: '0px 0px 25px 25px',
                    opacity: 75,
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderTop: '1px solid #B1B1B1'
                }, children: [_jsx(Typography, { variant: 'subtitle2', color: '#555555', children: "Latest approved assignment" }), _jsx(Tooltip, { title: freelancer.currentAssignmentName ? `Client: ${freelancer.currentAssignmentName}` : '', arrow: true, children: _jsx(Typography, { fontSize: '24px', fontWeight: 550, color: '#141414', sx: {
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                maxWidth: '80%'
                            }, children: freelancer.currentAssignmentName || 'None' }) })] })] }));
};
